/**
 * Hook for managing client search functionality
 * Consolidates client search and filter logic
 */

import React from "react";
import { useContacts, useClientSearch } from "@/lib/hooks/use-api";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";
import type { ClientRecord } from "@/lib/types/grant-access";

export function useClientSearchHook() {
  const {
    searchValue: clientSearchTerm,
    debouncedSearchValue: debouncedClientSearch,
    setSearchValue: setClientSearchTerm,
  } = useDebouncedSearch({
    initialValue: "",
    delay: 300,
  });

  const { data: initialClients, isLoading: initialClientsLoading } = useContacts({
    isActive: true,
    limit: 30,
  });

  const { data: searchResults, isFetching: isClientSearching } =
    useClientSearch(debouncedClientSearch);

  const clients = debouncedClientSearch ? searchResults || [] : initialClients || [];
  const clientsLoading = initialClientsLoading || isClientSearching;

  const displayedClients = React.useMemo(() => {
    const list = clients || [];
    const hasSearch = (clientSearchTerm || "").trim().length > 0;
    return hasSearch ? list : list.slice(0, 10);
  }, [clients, clientSearchTerm]);

  return {
    clientSearchTerm,
    debouncedClientSearch,
    setClientSearchTerm,
    clients,
    clientsLoading,
    displayedClients,
    isClientSearching,
  };
}
